import React, { useEffect, useMemo, useState, useCallback } from "react";
import { SideNav } from "components";
import {
  BaseDropdown,
  BaseInput,
  LargeButton,
  PageTitle,
  Ptag,
} from "components/core";
import { Formik, Form, FieldArray } from "formik";
import { tpaSetupScheme } from "pages/FlexSetup/schemas";
import TpaEscalation from "./TpaEscalation";
import { Box } from "@mui/material";
import http from "services/httpService";
import { DisplayName } from "utlis/utils";
import { HomeWrapper } from "components/CdBalance/style";
import TpaCashless from "./TpaCashless";
import _ from "lodash";
import { useLocation } from "react-router-dom";

const TpaSetup = ({ handleSubmit }) => {
  const [tpas, setTpas] = useState([]);
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  // Memoized helper functions
  const getOptions = useCallback((options) => {
    return options.map(({ display_name, tpa_id }) => ({
      label: display_name,
      value: tpa_id,
    }));
  }, []);

  // Safe localStorage access with error handling
  const getStoredValues = useCallback(() => {
    try {
      const selectedValues = localStorage.getItem("eb_selected_values");
      const userplanId = localStorage.getItem("eb_current_plan");
      
      return {
        selectedValues: selectedValues ? JSON.parse(selectedValues) : null,
        userplanId
      };
    } catch (error) {
      console.error("Error accessing localStorage:", error);
      return { selectedValues: null, userplanId: null };
    }
  }, []);

  const { selectedValues, userplanId } = getStoredValues();
  const prefillSupport = location.state?.tpaPrefill;
  const productLabelFromState = location.state?.productLabel || "";

  // Memoized product name
  const productName = useMemo(() => {
    if (!selectedValues || !userplanId) return productLabelFromState;
    
    const userplanData = selectedValues.userplans_data?.[userplanId];
    if (!userplanData) return productLabelFromState;
    
    const name = userplanData.product_type?.display_name || 
                 userplanData.product_type?.product_type_name;
    return name ? DisplayName(name) : productLabelFromState;
  }, [productLabelFromState, userplanId, selectedValues]);

  // API call to fetch TPAs
  const fetchTpas = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await http.get("/business/get_tpa_selectables/");
      setTpas(data || []);
    } catch (err) {
      console.error("Error fetching TPAs:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTpas();
  }, [fetchTpas]);

  // Helper functions for processing data
  const processArrayData = useCallback((data, levelKey) => {
    if (!data || !Array.isArray(data)) return [[{}]];
    
    const grouped = _.groupBy(data, levelKey);
    return Object.values(grouped);
  }, []);

  const cashlessClaims = useCallback((tpaData) => {
    return processArrayData(tpaData?.cashless_details, 'cashless_level');
  }, [processArrayData]);

  const escalationDetails = useCallback((tpaData) => {
    return processArrayData(tpaData?.escalation_details, 'escalation_level');
  }, [processArrayData]);

  // Memoized initial values
  const initialValues = useMemo(() => {
    const tpaIdFromSupport =
      prefillSupport?.claims_details?.tpa_id ||
      tpas.find(
        ({ display_name }) =>
          display_name === prefillSupport?.claims_details?.tpa_name
      )?.tpa_id ||
      "";

    if ((!selectedValues || !userplanId) && !prefillSupport) {
      return {
        tpa_id: "",
        tpa_userplan_unique_code: "",
        cashless: [[{}]],
        escalation_details: [[{}]],
      };
    }

    const tpaDetails =
      prefillSupport?.claims_details ||
      selectedValues?.userplans_data?.[userplanId]?.userplan_tpa_data;
    
    const baseValues = {
      tpa_id: tpaDetails?.tpa_id || tpaIdFromSupport,
      tpa_userplan_unique_code:
        tpaDetails?.tpa_userplan_unique_code ||
        (prefillSupport ? tpaDetails?.partner_id : "") ||
        "",
      cashless: cashlessClaims(tpaDetails),
      escalation_details: escalationDetails(tpaDetails),
    };

    // Handle escalation contacts if they exist
    if (tpaDetails?.escalation_contacts && Array.isArray(tpaDetails.escalation_contacts)) {
      baseValues.escalation_details = tpaDetails.escalation_contacts.map((detail) => ({
        escalation_contact_name: detail?.escalation_contact_name || "",
        escalation_contact_email: detail?.escalation_contact_email || "",
        escalation_contact: detail?.escalation_contact || "",
        escalation_region: detail?.escalation_region || "",
      }));
    }

    return baseValues;
  }, [
    selectedValues,
    userplanId,
    cashlessClaims,
    escalationDetails,
    prefillSupport,
    tpas,
  ]);

  // Form submission handler
  const handleFormSubmit = useCallback((values) => {
    try {
      const processedValues = { ...values };
      
      // Add escalation levels
      processedValues.escalation_details = values.escalation_details.map(
        (innerArray, outerIndex) =>
          innerArray.map((item) => ({
            ...item,
            escalation_level: outerIndex + 1,
          }))
      );
      
      // Add cashless levels
      processedValues.cashless = values.cashless.map(
        (innerArray, outerIndex) =>
          innerArray.map((item) => ({
            ...item,
            cashless_level: outerIndex + 1,
          }))
      );

      handleSubmit(processedValues);
    } catch (error) {
      console.error("Error processing form submission:", error);
    }
  }, [handleSubmit]);

  if ((!selectedValues || !userplanId) && !prefillSupport) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <Ptag>Unable to load configuration. Please try refreshing the page.</Ptag>
      </div>
    );
  }

  return (
    <>
      <SideNav />
      <HomeWrapper>
        <PageTitle title={`Setup ${productName}`} />
        <section style={{ maxWidth: "800px" }}>
          <Ptag fontSize="20px" fontWeight="700" marginTop="4rem">
            TPA Details
          </Ptag>
          
          <Formik
            initialValues={initialValues}
            validationSchema={tpaSetupScheme}
            onSubmit={handleFormSubmit}
            enableReinitialize
            validateOnChange={true}
            validateOnBlur={true}
            validateOnMount={true}
          >
            {(formikProps) => (
              <Form autoComplete="off">
                {/* TPA Selection Section */}
                <div style={{ margin: "36px 0rem" }}>
                  <BaseDropdown
                    fontSize="14"
                    staticLabel="Select TPA"
                    options={getOptions(tpas)}
                    formikProps={{ ...formikProps, name: "tpa_id" }}
                    disabled={loading || !!prefillSupport}
                  />
                  
                  <Box sx={{ display: 'flex', height: "16px" }} />
                  
                  <BaseInput
                    style={{ marginTop: "24px" }}
                    staticLabel="Partner Corporate ID (optional)"
                    type="text"
                    preventSpaces
                    fontSize="16px !important"
                    formikProps={{
                      ...formikProps,
                      name: "tpa_userplan_unique_code",
                    }}
                  />
                </div>

                {/* Cashless Contact Details Section */}
                <section style={{ marginTop: "6rem", backgroundColor: "#F6F3FF", padding: "24px 36px 24px 24px", borderRadius: "10px" }}>
                  <Ptag fontSize="16px" fontWeight="700" marginBottom="2rem">
                    Contact Details For Claim
                  </Ptag>
                  <FieldArray
                    name="cashless"
                    render={({ push, pop }) => (
                      <div style={{ marginBottom: "12px" }}>
                        {formikProps?.values?.cashless.map((_, index) => (
                          <TpaCashless 
                            key={index}
                            formikProps={formikProps} 
                            index={index} 
                          />
                        ))}
                        <div style={{ display: "flex", flexDirection: "row-reverse" }}>
                          <LargeButton
                            onClick={() => push([{}])}
                            fontSize="16px"
                            borderRadius="10px"
                            label="Add Level"
                            marginTop="16px"
                            style={{ marginBottom: "12px", width: '150px', height: '48px', gap: 16 }}
                          />
                          
                          {formikProps?.values?.cashless?.length > 1 && (
                            <LargeButton
                              onClick={() => pop()}
                              fontSize="16px"
                              borderRadius="10px"
                              label="Remove Level"
                              marginTop="16px"
                              style={{
                                marginBottom: "12px",
                                width: '150px',
                                height: '48px',
                                backgroundColor: "#F5F3FF",
                                color: "black",
                                border: "1px solid #231a3d",
                                marginRight: '16px'
                              }}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  />
                </section>

                {/* Escalation Contact Details Section */}
                <section style={{ marginTop: "6rem", backgroundColor: "#F6F3FF", padding: "24px 36px 24px 24px", borderRadius: "10px" }}>
                  <Ptag fontSize="16px" fontWeight="700" marginBottom="2rem">
                    Contact Details Escalation
                  </Ptag>
                  <FieldArray
                    name="escalation_details"
                    render={({ push, pop }) => (
                      <div style={{ marginBottom: "12px" }}>
                        {formikProps?.values?.escalation_details.map((_, index) => (
                          <TpaEscalation 
                            key={index}
                            formikProps={formikProps} 
                            index={index} 
                          />
                        ))}
                        <div style={{ display: "flex", flexDirection: "row-reverse" }}>
                          <LargeButton
                            onClick={() => push([{}])}
                            fontSize="16px"
                            borderRadius="10px"
                            label="Add Level"
                            marginTop="16px"
                            style={{ marginBottom: "12px", width: '150px', height: '48px', gap: 16 }}
                          />
                          
                          {formikProps?.values?.escalation_details?.length > 1 && (
                            <LargeButton
                              onClick={() => pop()}
                              fontSize="16px"
                              borderRadius="10px"
                              label="Remove Level"
                              marginTop="16px"
                              style={{
                                marginBottom: "12px",
                                width: '150px',
                                height: '48px',
                                backgroundColor: "#F5F3FF",
                                color: "black",
                                border: "1px solid #231a3d",
                                marginRight: '16px'
                              }}
                            />
                          )}
                        </div>
                      </div>
                    )}
                  />
                </section>

                {/* Submit Button */}
                <div style={{ marginTop: "7rem" }}>
                  <LargeButton
                    type="submit"
                    label="Save & Next"
                    disabled={!formikProps.isValid || loading}
                    style={{
                      padding: "2rem 0rem",
                      opacity: (formikProps.isValid && !loading) ? 1 : 0.6,
                      pointerEvents: (formikProps.isValid && !loading) ? "auto" : "none",
                    }}
                  />
                </div>
              </Form>
            )}
          </Formik>
        </section>
      </HomeWrapper>
    </>
  );
};

export default TpaSetup;